import React, { useMemo } from 'react';
import { X, GitBranch, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../../component-styles/RecordPages.css';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

export default function RetainerFolioModal({ retainer, projectId, onClose }) {
  /* --------------------------------------------------------
     DERIVED DATA (HOOKS MUST BE FIRST)
  -------------------------------------------------------- */
  const appliedDocs = useMemo(() => {
    if (!retainer) return [];

    // Preferred: explicit applied docs array (future-proof)
    if (Array.isArray(retainer.appliedDocs)) {
      return retainer.appliedDocs;
    }

    // Fallback: infer applications from paymentHistory
    if (Array.isArray(retainer.paymentHistory)) {
      return retainer.paymentHistory
        .filter((p) => p.paymentType === 'application')
        .map((p) => ({
          docType: p.targetType,
          docId: p.targetId,
          docNumber: p.reference || '—',
          amount: p.amount,
          date: p.date,
        }));
    }

    return [];
  }, [retainer]);

  /* --------------------------------------------------------
     EARLY EXIT (AFTER HOOKS)
  -------------------------------------------------------- */
  if (!retainer) return null;

  const funded = Number(retainer.payments || 0);
  const applied = Number(retainer.appliedAmount || 0);
  const available = Number(retainer.availableBalance || funded - applied || 0);

  /* --------------------------------------------------------
     RENDER
  -------------------------------------------------------- */
  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {retainer.retainerNumber}
          <span className="folio-sub">{retainer.description}</span>
        </h3>

        {/* SUMMARY */}
        <div className="folio-summary">
          <div>
            <label>Total</label>
            <div>{formatCurrency(retainer.amount)}</div>
          </div>

          <div>
            <label>Funded</label>
            <div>{formatCurrency(funded)}</div>
          </div>

          <div>
            <label>Applied</label>
            <div>{formatCurrency(applied)}</div>
          </div>

          <div>
            <label>Available</label>
            <div>{formatCurrency(available)}</div>
          </div>
        </div>

        {/* APPLICATIONS */}
        <h4 className="folio-section-label">Applied To</h4>

        <div className="folio-applied-list">
          {appliedDocs.length === 0 ? (
            <div className="folio-empty">No applications yet.</div>
          ) : (
            appliedDocs.map((doc, idx) => {
              const type = doc.docType?.toLowerCase();
              let url = '#';

              if (type === 'invoice') {
                url = `/projects/${projectId}/invoices/${doc.docId}`;
              } else if (type === 'proposal') {
                url = `/projects/${projectId}/proposals/${doc.docId}`;
              } else if (type === 'purchase-order') {
                url = `/projects/${projectId}/purchase-orders/${doc.docId}`;
              } else if (type === 'timebilling') {
                url = `/projects/${projectId}/timebilling-invoices/${doc.docId}`;
              }

              return (
                <Link to={url} key={idx} className="folio-applied-row">
                  <GitBranch size={16} className="folio-row-icon" />

                  <div className="folio-row-text">
                    <b>{doc.docNumber || '—'}</b> —{' '}
                    {type === 'timebilling'
                      ? 'Time Billing Invoice'
                      : doc.docType?.charAt(0).toUpperCase() + doc.docType?.slice(1)}
                    <span style={{ marginLeft: 6 }}>({formatCurrency(doc.amount)})</span>
                  </div>

                  <ExternalLink size={14} className="folio-open-icon" />
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
